import { FC, memo, ReactElement } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as ArrowLeftIcon } from 'assets/images/icons/arrow-left.svg';

import Header from 'ui/Header';
import { ButtonSquare } from 'ui/Button/ButtonSquare';

import { PAGES } from 'utils/link';
import { isUserCreateFormFilled, QueryHelper } from 'utils/helpers';
import { showModal } from 'store/Modal/Modal.action';
import { IRootStore } from 'store/Root.reducer';
import { ModalType, Role } from 'entities/common';
import { QUERY_PARAMS } from 'utils/constants';
import { resetUserCreateData } from 'store/UserCreate/UserCreate.action';
import { resetUserUpdateData } from 'store/UserUpdate/UserUpdate.action';
import { IUserUpdateState } from 'store/UserUpdate/UserUpdate.type';

type PropsType = {
  title?: string;
  children: ReactElement;
  showBackButton?: boolean;
  onBackClick?: () => void;
};

const LayoutDashboard: FC<PropsType> = ({
  title,
  children,
  showBackButton = true,
  onBackClick,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userData, modalState, auth } = useSelector((state: IRootStore) => ({
    auth: state.auth,
    modalState: state.modal,
    userData: state[id ? 'userUpdate' : 'userCreate'],
  }));

  const isUser = auth.user?.role === Role.USER;
  const exception = isUser && location.pathname.startsWith(PAGES.DASHBOARD?.split("/")?.slice(0,2).join("/"));

  const handleBack = () => {
    if (modalState.active && modalState.modalType !== ModalType.PAGE_LEAVE) {
      return;
    }

    if (onBackClick) {
      onBackClick();
      return;
    }

    if (location.pathname.startsWith(PAGES.USER_CREATE)) {
      const { [QUERY_PARAMS.STEP]: currentStep } =
        QueryHelper.getFromQueryString(location?.search);
      const stepNumber = Number(currentStep) || 1;

      if (stepNumber === 2) {
        navigate(
          PAGES.USER_CREATE +
          QueryHelper.getQueryString({
            [QUERY_PARAMS.STEP]: 1,
          }),
        );
        return;
      }

      const isFormFilled = isUserCreateFormFilled(userData);

      if (isFormFilled) {
        dispatch(
          showModal({
            modalType: ModalType.PAGE_LEAVE,
            modalProps: {
              onConfirm: () => {
                dispatch(resetUserCreateData());
                navigate(PAGES.ADMIN_USERS, { replace: true });
              },
            },
          }),
        );
      } else {
        if (stepNumber === 1) {
          dispatch(resetUserCreateData());
        }

        navigate(PAGES.ADMIN_USERS, { replace: true });
      }
      return;
    }

    const userUpdateBasePath = PAGES.USER_UPDATE.split(':id')[0];
    if (location.pathname.startsWith(userUpdateBasePath)) {
      const { [QUERY_PARAMS.STEP]: currentStep } =
        QueryHelper.getFromQueryString(location?.search);
      const stepNumber = Number(currentStep) || 1;

      const match = location.pathname.match(/\/user\/update\/([^/]+)/);
      const userIdFromPath = match?.[1];

      if (stepNumber === 2) {
        navigate(
          PAGES.USER_UPDATE.replace(':id', userIdFromPath || '') +
          QueryHelper.getQueryString({
            [QUERY_PARAMS.STEP]: 1,
          }),
        );
        return;
      }

      const isFormChanged = (userData as IUserUpdateState)?.isFormChanged;

      if (isFormChanged) {
        dispatch(
          showModal({
            modalType: ModalType.PAGE_LEAVE,
            modalProps: {
              onConfirm: () => {
                dispatch(resetUserUpdateData());
                if (userIdFromPath) {
                  localStorage.removeItem(`userUpdateData_${userIdFromPath}`);
                }
                navigate(PAGES.ADMIN_USERS, { replace: true });
              },
            },
          }),
        );
      } else {
        if (stepNumber === 1) {
          dispatch(resetUserUpdateData());
        }

        navigate(PAGES.ADMIN_USERS, { replace: true });
      }
      return;
    }

    if (window?.history?.length > 1) {
      navigate(-1);
    } else {
      navigate(PAGES.AUTH_LOGIN, { replace: true });
    }
  };

  return (
    <div className="layout-dashboard">
      <Header />

      <main className="main">
        <div className="container">
          {children}
        </div>
      </main>
    </div>
  );
};

export default memo(LayoutDashboard);
